import React from 'react';
import { Link } from 'react-router';
import { trackEvent } from '../analytics';
import {
  createFlyMateHttpError,
  FlyMateRequestError,
  flyMateMessageLabel,
  hasCurrentFlyMateRecoveredAnswer,
  hasFlyMateAnswer,
  isFlyMateFailureReason,
  isFlyMateSessionError,
  isFlyMateTimeoutError,
  parseFlyMateResponse,
  readFlyMateResponse,
  shouldRecoverFlyMateRequest,
  shouldRetryFlyMateRecovery,
} from './requestState';

type ChatSource = {
  documentName?: string;
  page?: number;
  citation?: string;
};

type ArtifactDownloadFormat = 'md' | 'docx' | 'pdf' | 'xlsx' | 'pptx' | 'txt' | 'html' | 'csv' | 'json' | 'xml';

type ChatArtifact = {
  requested: boolean;
  format: ArtifactDownloadFormat | null;
  filename: string;
  title: string;
  contentMarkdown: string;
};

type ChatAttachment = {
  name: string;
  size?: number;
  type?: string;
};

type ChatMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  attachments?: ChatAttachment[];
  grounded?: boolean;
  transportError?: boolean;
  reason?: string;
  sources?: ChatSource[];
  artifact?: ChatArtifact;
  questionId?: string;
  feedback?: {
    rating?: 'up' | 'down';
    why?: string;
    expectedAnswer?: string;
    submittedAt?: string;
  };
};

type TryAiResponse = {
  ok?: boolean;
  grounded?: boolean;
  answer?: string;
  reason?: string;
  error?: string;
  sources?: ChatSource[];
  artifact?: ChatArtifact;
  questionId?: string;
  usage?: FlyMateUsageSummary;
};

type FlyMateLead = {
  leadId: string;
  name: string;
  surname: string;
  email: string;
  organization: string;
  jobTitle?: string;
  consents?: {
    termsAccepted?: boolean;
    communications?: boolean;
  };
  city?: string;
  country?: string;
  access?: {
    emailVerified?: boolean;
    blocked?: boolean;
    blockedReason?: string;
  };
};

type FlyMateLeadResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  verificationRequired?: boolean;
  lead?: FlyMateLead;
  session?: {
    sessionId?: string;
    pagePath?: string;
  };
};

type FlyMateUser = {
  userId: string;
  email: string;
  name: string;
  surname: string;
  organization: string;
  jobTitle?: string;
  leadId: string;
  billing?: {
    mode?: string;
    freeAccess?: boolean;
    stripeEnabled?: boolean;
    status?: string;
    planId?: string;
  };
};

type FlyMateConfig = {
  billing?: {
    mode?: string;
    freeAccess?: boolean;
    stripeEnabled?: boolean;
    plans?: Array<{ id: string; name: string; monthlyTokenAllowance?: number; enabled?: boolean }>;
  };
  upload?: {
    maxFiles?: number;
    maxFileSizeBytes?: number;
    maxFileSizeMb?: number;
  };
  socialAuth?: {
    google?: boolean;
    github?: boolean;
    facebook?: boolean;
    linkedin?: boolean;
  };
};

type SocialProvider = 'google' | 'github' | 'facebook' | 'linkedin';

type FlyMateUsageSummary = {
  requests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  billableTokens: number;
};

type FlyMateChatSummary = {
  sessionId: string;
  title: string;
  chatHistoryId?: string;
  updatedAt?: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const normalizeLeadEmail = (value: string) => value.trim().toLowerCase();
const looksLikeObjectId = (value?: string) => /^[a-f0-9]{24}$/i.test(String(value || '').trim());
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);
const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="currentColor">
    <path d="M12 .5a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.04c-3.34.73-4.04-1.42-4.04-1.42-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5.99.11-.78.42-1.3.76-1.6-2.67-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.3-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.45 11.45 0 0 1 6.01 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.25 2.88.12 3.18.77.84 1.23 1.91 1.23 3.22 0 4.61-2.81 5.63-5.49 5.93.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .5Z" />
  </svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="#1877F2">
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.03 1.79-4.7 4.53-4.7 1.31 0 2.69.24 2.69.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z" />
  </svg>
);
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="#0A66C2">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.32 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.1 20.45H3.54V9H7.1v11.45ZM22.23 0H1.76C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.76 24h20.47c.97 0 1.77-.77 1.77-1.72V1.72C24 .77 23.2 0 22.23 0Z" />
  </svg>
);

export const FLYMATE_SESSION_STORAGE_KEY_PREFIX = 'flymate-session-chat-v1';
export const FLYMATE_LEAD_STORAGE_KEY = 'flymate-session-lead-v1';
export const FLYMATE_CHAT_SESSION_ID_KEY = 'flymate-session-id-v1';

const FLYMATE_REQUEST_HISTORY_TURNS = 4;
const FLYMATE_REQUEST_HISTORY_CONTENT_CHARS = 1600;

const getFlyMateChatStorageKey = (sessionId: string) => `${FLYMATE_SESSION_STORAGE_KEY_PREFIX}:${sessionId}`;

const trimFlyMateRequestContent = (content: string) => {
  const normalized = content.replace(/\s+/g, ' ').trim();
  if (normalized.length <= FLYMATE_REQUEST_HISTORY_CONTENT_CHARS) return normalized;

  return `${normalized.slice(0, FLYMATE_REQUEST_HISTORY_CONTENT_CHARS - 1).trim()}…`;
};

const buildFlyMateRequestHistory = (messages: ChatMessage[]) =>
  messages
    .filter((entry) => !entry.transportError && !isFlyMateFailureReason(entry.reason))
    .slice(-FLYMATE_REQUEST_HISTORY_TURNS)
    .map((entry) => ({
      role: entry.role,
      content: trimFlyMateRequestContent(entry.content),
    }))
    .filter((entry) => entry.content);

const STARTER_PROMPTS = [
  'Explain the operational difference between METAR, TAF, SIGMET, AIRMET, ASHTAM, and SNOWTAM.',
  'What does ICAO require for eAIP and AIRAC publication control?',
  'Give me a grounded overview of how PANS-OPS, Annex 4, and Annex 15 relate in procedure publication.',
  'What is the role of aeronautical information services inside a digital aviation system?',
];

const AVIAGOV_LOGO_URL = 'https://aviagov.aero/AviaGov.svg';

const createBrowserSessionId = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `flymate-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createFlyMateRequestId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === 'function') globalThis.crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const buildAssistantGreeting = (name?: string): ChatMessage => ({
  id: `assistant-initial-${name || 'guest'}`,
  role: 'assistant',
  grounded: true,
  content: name
    ? `Hi ${name}, how can I help you?`
    : "I'm FlyMate. Before we start, please introduce yourself so I can open your session.",
});

const summarizeChatLabel = (value: string) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Untitled chat';
  return text.length > 42 ? `${text.slice(0, 42)}…` : text;
};

const formatLeadName = (lead: FlyMateLead | null) => {
  if (!lead) return 'Guest';
  return [lead.name, lead.surname].filter(Boolean).join(' ') || lead.organization || 'Guest';
};

const ARTIFACT_DOWNLOAD_FORMATS: Array<{ format: ArtifactDownloadFormat; label: string }> = [
  { format: 'md', label: 'MD' },
  { format: 'docx', label: 'DOCX' },
  { format: 'pdf', label: 'PDF' },
  { format: 'xlsx', label: 'XLSX' },
  { format: 'pptx', label: 'PPTX' },
  { format: 'txt', label: 'TXT' },
  { format: 'html', label: 'HTML' },
  { format: 'csv', label: 'CSV' },
  { format: 'json', label: 'JSON' },
  { format: 'xml', label: 'XML' },
];

const normalizeArtifact = (artifact?: ChatArtifact): ChatArtifact | undefined => {
  if (!artifact?.requested || !artifact.contentMarkdown?.trim()) return undefined;
  const requestedFormat = ARTIFACT_DOWNLOAD_FORMATS.some((item) => item.format === artifact.format)
    ? artifact.format
    : 'md';
  const extension = requestedFormat || 'md';
  const baseFilename = artifact.filename?.trim() || artifact.title?.trim() || 'flymate-document';
  const filename = baseFilename
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return {
    requested: true,
    format: requestedFormat,
    filename: filename.endsWith(`.${extension}`) ? filename : `${filename || 'flymate-document'}.${extension}`,
    title: artifact.title?.trim() || 'Prepared document',
    contentMarkdown: artifact.contentMarkdown.trim(),
  };
};

const downloadArtifact = async (artifact: ChatArtifact, format: ArtifactDownloadFormat) => {
  const normalized = normalizeArtifact(artifact);
  if (!normalized || typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    const response = await fetch('/api/flymate/artifact/convert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        format,
        title: normalized.title,
        filename: normalized.filename,
        contentMarkdown: normalized.contentMarkdown,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.fileBase64) {
      throw new Error(data?.error || 'Could not prepare the download.');
    }
    const bytes = Uint8Array.from(window.atob(data.fileBase64), (char) => char.charCodeAt(0));
    const blob = new Blob([bytes], { type: data.mimeType || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = data.filename || normalized.filename.replace(/\.[a-z0-9]+$/i, `.${format}`);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  } catch (downloadError) {
    console.error('FlyMate artifact download failed:', downloadError);
    const fallback = new Blob([normalized.contentMarkdown], { type: 'text/markdown;charset=utf-8' });
    const url = window.URL.createObjectURL(fallback);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = normalized.filename.replace(/\.[a-z0-9]+$/i, '.md');
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }
};

const FlyMateShell: React.FC = () => {
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [lead, setLead] = React.useState<FlyMateLead | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = React.useState(false);
  const [isResolvingSession, setIsResolvingSession] = React.useState(true);
  const [leadForm, setLeadForm] = React.useState({
    name: '',
    surname: '',
    email: '',
    organization: '',
    jobTitle: '',
    combinedConsent: false,
  });
  const [leadError, setLeadError] = React.useState<string | null>(null);
  const [isLeadSubmitting, setIsLeadSubmitting] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [verificationPending, setVerificationPending] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isRecoveringAnswer, setIsRecoveringAnswer] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [currentUser, setCurrentUser] = React.useState<FlyMateUser | null>(null);
  const [flyMateConfig, setFlyMateConfig] = React.useState<FlyMateConfig | null>(null);
  const [savedChats, setSavedChats] = React.useState<FlyMateChatSummary[]>([]);
  const [authMode, setAuthMode] = React.useState<'login' | 'register'>('register');
  const [authForm, setAuthForm] = React.useState({ email: '', password: '' });
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [isAuthSubmitting, setIsAuthSubmitting] = React.useState(false);
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);
  const [usageSummary, setUsageSummary] = React.useState<FlyMateUsageSummary | null>(null);
  const [passwordForm, setPasswordForm] = React.useState({ currentPassword: '', newPassword: '' });
  const [profileNotice, setProfileNotice] = React.useState<string | null>(null);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = React.useState(false);
  const [feedbackDrafts, setFeedbackDrafts] = React.useState<Record<string, { why: string; expectedAnswer: string; expanded: boolean; saving?: boolean }>>({});
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const sessionIdRef = React.useRef<string>('');

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSubmitting]);

  const loadSavedChats = React.useCallback(async () => {
    try {
      const response = await fetch('/api/flymate/chats');
      const data = (await response.json().catch(() => ({}))) as { chats?: FlyMateChatSummary[] };
      if (response.ok && Array.isArray(data.chats)) {
        setSavedChats(data.chats);
      }
    } catch {
      // Saved chats are optional while FlyMate remains free for guests.
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    void fetch('/api/flymate/config')
      .then((response) => response.json())
      .then((data) => setFlyMateConfig(data || null))
      .catch(() => undefined);
    void fetch('/api/flymate/auth/me')
      .then((response) => response.json())
      .then((data) => {
        if (data?.user) {
          setCurrentUser(data.user);
          setAuthForm((current) => ({ ...current, email: data.user.email || current.email }));
          if (data.user.leadId) {
            setLead({
              leadId: data.user.leadId,
              name: data.user.name || '',
              surname: data.user.surname || '',
              email: data.user.email || '',
              organization: data.user.organization || '',
              jobTitle: data.user.jobTitle || '',
            });
          }
          void loadSavedChats();
        }
      })
      .catch(() => undefined);
  }, [loadSavedChats]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const restoreMessagesForSession = (sessionId: string) => {
      try {
        const raw = window.localStorage.getItem(getFlyMateChatStorageKey(sessionId));
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || !parsed.length) return;
        const restored = parsed
          .filter((entry) => entry && (entry.role === 'assistant' || entry.role === 'user') && typeof entry.content === 'string')
          .map((entry) => ({
            id: typeof entry.id === 'string' ? entry.id : `${entry.role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            role: entry.role,
            content: entry.content,
            attachments: Array.isArray(entry.attachments)
              ? entry.attachments
                  .filter((attachment) => attachment && typeof attachment.name === 'string')
                  .map((attachment) => ({
                    name: attachment.name,
                    size: typeof attachment.size === 'number' ? attachment.size : undefined,
                    type: typeof attachment.type === 'string' ? attachment.type : undefined,
                  }))
              : [],
            grounded: typeof entry.grounded === 'boolean' ? entry.grounded : undefined,
            transportError: entry.transportError === true,
            reason: typeof entry.reason === 'string' ? entry.reason : undefined,
            sources: Array.isArray(entry.sources) ? entry.sources : [],
            questionId: typeof entry.questionId === 'string' ? entry.questionId : undefined,
            feedback: entry.feedback && typeof entry.feedback === 'object' ? entry.feedback : undefined,
          }));
        if (restored.length) {
          setMessages(restored);
        }
      } catch {
        // Ignore corrupted chat cache.
      }
    };

    const boot = async () => {
      let resolvedSessionId = '';
      let restoredLead: FlyMateLead | null = null;

      try {
        const existingSessionId = window.localStorage.getItem(FLYMATE_CHAT_SESSION_ID_KEY);
        resolvedSessionId = existingSessionId || createBrowserSessionId();
        sessionIdRef.current = resolvedSessionId;
        window.localStorage.setItem(FLYMATE_CHAT_SESSION_ID_KEY, resolvedSessionId);

        const rawLead = window.localStorage.getItem(FLYMATE_LEAD_STORAGE_KEY);
        if (rawLead) {
          const parsedLead = JSON.parse(rawLead);
          if (
            parsedLead &&
            typeof parsedLead.leadId === 'string' &&
            looksLikeObjectId(parsedLead.leadId) &&
            typeof parsedLead.name === 'string'
          ) {
            restoredLead = parsedLead;
            setLead(parsedLead);
          } else {
            window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
          }
        }

        if (resolvedSessionId) {
          try {
            const response = await fetch('/api/try-ai/session/resolve', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ sessionId: resolvedSessionId }),
            });

            if (response.ok) {
              const data = (await response.json()) as FlyMateLeadResponse;
              if (data?.lead?.leadId) {
                restoredLead = data.lead;
                setLead(data.lead);
              } else if (restoredLead) {
                restoredLead = null;
                setLead(null);
                window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
              }
            } else if (response.status === 403) {
              restoredLead = null;
              setLead(null);
              setLeadError('Your FlyMate access has been blocked. Please contact AviaGov.');
              window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
            } else if (response.status === 404 && restoredLead) {
              restoredLead = null;
              setLead(null);
              window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
            }
          } catch {
            // Ignore resolve failures and fall back to local storage.
          }
        }

        if (restoredLead) {
          restoreMessagesForSession(resolvedSessionId);
        } else {
          setMessages([]);
        }
      } catch {
        // Ignore corrupted storage.
      } finally {
        setIsResolvingSession(false);
      }
    };

    void boot();
  }, []);

  React.useEffect(() => {
    if (!messages.length && lead?.name) {
      setMessages([buildAssistantGreeting(lead.name)]);
    }
  }, [lead, messages.length]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!sessionIdRef.current) return;
      window.localStorage.setItem(getFlyMateChatStorageKey(sessionIdRef.current), JSON.stringify(messages));
    } catch {
      // Ignore storage errors.
    }
  }, [messages]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (lead) {
        window.localStorage.setItem(FLYMATE_LEAD_STORAGE_KEY, JSON.stringify(lead));
      } else {
        window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors.
    }
  }, [lead]);

  const resetChat = React.useCallback(() => {
    setMessages(lead?.name ? [buildAssistantGreeting(lead.name)] : []);
    setInput('');
    setError(null);
    if (typeof window !== 'undefined') {
      try {
        if (sessionIdRef.current) {
          window.localStorage.removeItem(getFlyMateChatStorageKey(sessionIdRef.current));
        }
      } catch {
        // Ignore storage errors.
      }
    }
  }, [lead]);

  const startNewChatSession = React.useCallback(async () => {
    const nextSessionId = createBrowserSessionId();

    if (currentUser) {
      try {
        const response = await fetch('/api/flymate/chats', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: nextSessionId }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to create saved FlyMate chat.');
        }
        await loadSavedChats();
      } catch (sessionError) {
        setError(sessionError instanceof Error ? sessionError.message : 'Failed to create saved FlyMate chat.');
        return;
      }
    }

    if (lead?.leadId) {
      try {
        const response = await fetch('/api/try-ai/session/new', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            leadId: lead.leadId,
            sessionId: nextSessionId,
          }),
        });

        if (!response.ok) {
          const detail = await response.text();
          throw new Error(detail || 'Failed to create a new FlyMate chat session.');
        }
      } catch (sessionError) {
        const message = sessionError instanceof Error ? sessionError.message : 'Failed to create a new FlyMate chat session.';
        if (/blocked/i.test(message)) {
          setLead(null);
          if (typeof window !== 'undefined') {
            try {
              window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
            } catch {
              // Ignore storage cleanup errors.
            }
          }
        }
        setError(message);
        return;
      }
    }

    sessionIdRef.current = nextSessionId;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(FLYMATE_CHAT_SESSION_ID_KEY, nextSessionId);
      } catch {
        // Ignore storage errors.
      }
    }

    resetChat();
  }, [currentUser, lead, loadSavedChats, resetChat]);

  const submitLead = React.useCallback(async (mode: 'default' | 'request_verification' = 'default') => {
    if (isLeadSubmitting) return;

    const payload = {
      name: leadForm.name.trim(),
      surname: leadForm.surname.trim(),
      email: normalizeLeadEmail(leadForm.email),
      organization: leadForm.organization.trim(),
      jobTitle: leadForm.jobTitle.trim(),
      consents: {
        termsAccepted: leadForm.combinedConsent,
        communications: leadForm.combinedConsent,
      },
    };

    if (!payload.name || !payload.surname || !payload.email || !payload.organization) {
      setLeadError('Please complete your name, surname, work email, and organization.');
      return;
    }
    if (!EMAIL_PATTERN.test(payload.email)) {
      setLeadError('Please enter a valid work email address.');
      return;
    }
    if (!payload.consents.termsAccepted || !payload.consents.communications) {
      setLeadError('Please accept the Terms, Privacy Policy, and AviaGov email updates to continue.');
      return;
    }
    if (verificationPending && mode !== 'request_verification' && verificationCode.trim().length !== 6) {
      setLeadError('Enter the 6-digit verification code we sent to your email.');
      return;
    }

    setLeadError(null);
    setIsLeadSubmitting(true);

    try {
      const response = await fetch('/api/try-ai/lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          sessionId: sessionIdRef.current || createBrowserSessionId(),
          verificationCode: verificationPending && mode !== 'request_verification' ? verificationCode.trim() : undefined,
        }),
      });

      const rawText = await response.text();
      const data = (() => {
        try {
          return (rawText ? JSON.parse(rawText) : {}) as FlyMateLeadResponse;
        } catch {
          return {} as FlyMateLeadResponse;
        }
      })();

      if (response.ok && data?.verificationRequired && !data?.lead?.leadId) {
        setVerificationPending(true);
        setLeadError(data.message || 'We sent a verification code to your email.');
        return;
      }

      if (!response.ok || !data?.lead?.leadId) {
        throw new Error(data.error || 'Failed to start FlyMate.');
      }

      setLead(data.lead);
      setMessages([buildAssistantGreeting(data.lead.name)]);
      setVerificationPending(false);
      setVerificationCode('');
      setLeadError(null);
      setError(null);
      trackEvent('generate_lead', { form_id: 'flymate_intro_form', module: 'ai_assistant' });
    } catch (requestError) {
      setLeadError(
        requestError instanceof Error ? requestError.message : 'Failed to start FlyMate right now.'
      );
    } finally {
      setIsLeadSubmitting(false);
    }
  }, [isLeadSubmitting, leadForm, verificationCode, verificationPending]);

  const recoverAnswerFromSession = React.useCallback(async (message: string, requestId: string) => {
    if (!lead?.leadId || !sessionIdRef.current) return null;

    for (const delayMs of [900, 1800, 3200]) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      try {
        const response = await fetch('/api/try-ai/session/recover', {
          method: 'POST',
          signal: AbortSignal.timeout(15000),
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            leadId: lead.leadId,
            leadEmail: lead.email,
            sessionId: sessionIdRef.current,
            message,
            requestId,
          }),
        });

        const data = parseFlyMateResponse(await response.text());
        if (response.ok && hasCurrentFlyMateRecoveredAnswer(data, requestId)) {
          return data as TryAiResponse;
        }
        if (!shouldRetryFlyMateRecovery(response.status, data)) return null;
      } catch {
        // Best-effort recovery for mobile/proxy disconnects after the backend already logged an answer.
      }
    }

    return null;
  }, [lead]);

  const billingIsFree = flyMateConfig?.billing?.freeAccess !== false;
  const maxUploadFiles = Math.max(1, flyMateConfig?.upload?.maxFiles || 3);
  const maxUploadFileSizeBytes = Math.max(1024 * 1024, flyMateConfig?.upload?.maxFileSizeBytes || 10 * 1024 * 1024);
  const maxUploadFileSizeMb = Math.max(1, Math.floor(maxUploadFileSizeBytes / (1024 * 1024)));
  const canUploadFiles = ['active', 'trialing'].includes(currentUser?.billing?.status || '') && currentUser?.billing?.planId !== 'free_beta';

  const sendMessage = React.useCallback(
    async (rawMessage?: string) => {
      const message = (rawMessage ?? input).trim();
      if (!lead?.leadId || !message || isSubmitting) return;

      const filesToSend = selectedFiles;
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: message,
        attachments: filesToSend.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
        })),
      };

      const priorHistory = buildFlyMateRequestHistory(messages);
      const nextHistory = [...messages, userMessage];
      setMessages(nextHistory);
      setInput('');
      setSelectedFiles([]);
      setError(null);
      setIsSubmitting(true);
      setIsRecoveringAnswer(false);
      trackEvent('try_ai_question_submitted', { module: 'ai_assistant' });

      const requestId = createFlyMateRequestId();
      try {
        const hasFiles = filesToSend.length > 0;
        const requestHistory = hasFiles ? [] : priorHistory;
        const response = await fetch('/api/try-ai/ask', hasFiles
          ? {
              method: 'POST',
              signal: AbortSignal.timeout(370000),
              headers: { Accept: 'text/event-stream, application/json' },
              body: (() => {
                const formData = new FormData();
                formData.append('message', message);
                formData.append('requestId', requestId);
                if (requestHistory.length) {
                  formData.append('history', JSON.stringify(requestHistory));
                }
                formData.append('leadId', lead.leadId);
                formData.append('leadEmail', lead.email);
                formData.append('sessionId', sessionIdRef.current || '');
                filesToSend.forEach((file) => formData.append('files', file));
                return formData;
              })(),
            }
          : {
              method: 'POST',
              signal: AbortSignal.timeout(370000),
              headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream, application/json',
              },
              body: JSON.stringify({
                message,
                requestId,
                history: requestHistory,
                leadId: lead.leadId,
                leadEmail: lead.email,
                sessionId: sessionIdRef.current || undefined,
              }),
            });

        const result = await readFlyMateResponse(response);
        const data = (result.data || {}) as TryAiResponse;

        if (result.status < 200 || result.status >= 300) {
          if (isFlyMateSessionError(result.data)) {
            setLead(null);
            if (typeof window !== 'undefined') {
              try {
                window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
                if (sessionIdRef.current) {
                  window.localStorage.removeItem(getFlyMateChatStorageKey(sessionIdRef.current));
                }
              } catch {
                // Ignore storage cleanup errors.
              }
            }
          }

          throw createFlyMateHttpError(result.status, result.data, maxUploadFileSizeMb);
        }

        if (!hasFlyMateAnswer(result.data)) {
          throw new FlyMateRequestError('FlyMate did not return an answer. Please try again in a moment.', { recoverable: true });
        }

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          grounded: data.grounded === true,
          reason: typeof data.reason === 'string' ? data.reason : undefined,
          transportError: isFlyMateFailureReason(data.reason),
          questionId: typeof data.questionId === 'string' ? data.questionId : undefined,
          content:
            data.answer?.trim() ||
            'I could not produce a grounded answer from the current knowledge base.',
          sources: Array.isArray(data.sources) ? data.sources : [],
          artifact: normalizeArtifact(data.artifact),
        };

        if (data.usage) {
          setUsageSummary(data.usage);
        }

        setMessages((current) => [...current, assistantMessage]);
      } catch (requestError) {
        const canRecover = filesToSend.length === 0 && shouldRecoverFlyMateRequest(requestError);
        setIsRecoveringAnswer(canRecover);
        const recovered = canRecover ? await recoverAnswerFromSession(message, requestId) : null;
        if (recovered?.answer?.trim()) {
          setMessages((current) => [
            ...current,
            {
              id: `assistant-recovered-${Date.now()}`,
              role: 'assistant',
              grounded: recovered.grounded === true,
              reason: typeof recovered.reason === 'string' ? recovered.reason : undefined,
              transportError: isFlyMateFailureReason(recovered.reason),
              questionId: typeof recovered.questionId === 'string' ? recovered.questionId : undefined,
              content: recovered.answer.trim(),
              sources: Array.isArray(recovered.sources) ? recovered.sources : [],
              artifact: normalizeArtifact(recovered.artifact),
            },
          ]);
          return;
        }

        const messageText =
          requestError instanceof Error
            ? isFlyMateTimeoutError(requestError)
              ? 'FlyMate took too long to respond. Please try again in a moment.'
              : requestError instanceof TypeError
                ? 'FlyMate could not be reached. Please check your connection and try again.'
                : requestError.message
            : 'FlyMate is unavailable right now. Please try again in a moment.';
        setError(messageText);
        setInput(message);
        setSelectedFiles(filesToSend);
        setMessages((current) => [
          ...current,
          {
            id: `assistant-error-${Date.now()}`,
            role: 'assistant',
            grounded: false,
            transportError: true,
            content: messageText,
          },
        ]);
      } finally {
        setIsSubmitting(false);
        setIsRecoveringAnswer(false);
      }
    },
    [input, isSubmitting, lead, maxUploadFileSizeMb, messages, recoverAnswerFromSession, selectedFiles]
  );

  const recentPrompts = React.useMemo(
    () => messages.filter((message) => message.role === 'user').slice(-6).reverse(),
    [messages]
  );

  const loadChatMessages = React.useCallback(async (sessionId: string) => {
    try {
      const response = await fetch(`/api/flymate/chats/${encodeURIComponent(sessionId)}/messages`);
      const data = (await response.json().catch(() => ({}))) as { messages?: ChatMessage[] };
      if (response.ok && Array.isArray(data.messages) && data.messages.length) {
        setMessages(data.messages);
      } else {
        setMessages(lead?.name ? [buildAssistantGreeting(lead.name)] : []);
      }
    } catch {
      setMessages(lead?.name ? [buildAssistantGreeting(lead.name)] : []);
    }
  }, [lead]);

  const submitAuth = React.useCallback(async () => {
    if (isAuthSubmitting) return;
    setAuthError(null);
    setIsAuthSubmitting(true);
    try {
      const isRegister = authMode === 'register';
      const response = await fetch(isRegister ? '/api/flymate/auth/register' : '/api/flymate/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizeLeadEmail(authForm.email || lead?.email || leadForm.email || ''),
          password: authForm.password,
          name: lead?.name || leadForm.name,
          surname: lead?.surname || leadForm.surname,
          organization: lead?.organization || leadForm.organization,
          jobTitle: lead?.jobTitle || leadForm.jobTitle,
          role: lead?.jobTitle || leadForm.jobTitle,
          leadId: lead?.leadId || '',
          leadEmail: lead?.email || authForm.email || leadForm.email,
          sessionId: sessionIdRef.current || undefined,
          combinedConsent: leadForm.combinedConsent,
          consents: {
            termsAccepted: leadForm.combinedConsent,
            communications: leadForm.combinedConsent,
          },
        }),
      });
      const data = (await response.json().catch(() => ({}))) as { user?: FlyMateUser; error?: string };
      if (!response.ok || !data.user) {
        throw new Error(data.error || 'FlyMate account request failed.');
      }
      setCurrentUser(data.user);
      setAuthForm((current) => ({ ...current, password: '', email: data.user?.email || current.email }));
      if (data.user.leadId) {
        setLead({
          leadId: data.user.leadId,
          name: data.user.name || '',
          surname: data.user.surname || '',
          email: data.user.email || '',
          organization: data.user.organization || '',
        });
      }
      await loadSavedChats();
    } catch (authRequestError) {
      setAuthError(authRequestError instanceof Error ? authRequestError.message : 'FlyMate account request failed.');
    } finally {
      setIsAuthSubmitting(false);
    }
  }, [authForm, authMode, isAuthSubmitting, lead, leadForm, loadSavedChats]);

  const logout = React.useCallback(async () => {
    await fetch('/api/flymate/auth/logout', { method: 'POST' }).catch(() => undefined);
    setCurrentUser(null);
    setSavedChats([]);
  }, []);

  const openAccountAuth = React.useCallback((mode: 'login' | 'register') => {
    const currentLead = lead;
    setAuthMode(mode);
    setAuthError(null);
    setIsProfileOpen(false);
    if (currentLead) {
      setLeadForm((current) => ({
        ...current,
        name: current.name || currentLead.name || '',
        surname: current.surname || currentLead.surname || '',
        email: current.email || currentLead.email || '',
        organization: current.organization || currentLead.organization || '',
        jobTitle: current.jobTitle || currentLead.jobTitle || '',
      }));
      setAuthForm((current) => ({ ...current, email: current.email || currentLead.email || '' }));
    }
    setLead(null);
    setMessages([]);
    setVerificationPending(false);
    setVerificationCode('');
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(FLYMATE_LEAD_STORAGE_KEY);
        if (sessionIdRef.current) {
          window.localStorage.removeItem(getFlyMateChatStorageKey(sessionIdRef.current));
        }
      } catch {
        // Ignore storage cleanup errors.
      }
    }
  }, [lead]);

  const choosePlan = React.useCallback(async (planId: string) => {
    setAuthError(null);
    try {
      const response = await fetch('/api/flymate/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId }),
      });
      const data = (await response.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!response.ok || !data.url) throw new Error(data.error || 'Unable to open Stripe checkout.');
      window.location.href = data.url;
    } catch (planError) {
      setAuthError(planError instanceof Error ? planError.message : 'Unable to open Stripe checkout.');
    }
  }, []);

  const loadProfileUsage = React.useCallback(async () => {
    if (!currentUser) return;
    try {
      const response = await fetch('/api/flymate/profile');
      const data = (await response.json().catch(() => ({}))) as { usage?: FlyMateUsageSummary };
      if (response.ok && data.usage) setUsageSummary(data.usage);
    } catch {
      // Profile usage is optional and should not block chat access.
    }
  }, [currentUser]);

  React.useEffect(() => {
    if (isProfileOpen) void loadProfileUsage();
  }, [isProfileOpen, loadProfileUsage]);

  const changePassword = React.useCallback(async () => {
    if (isPasswordSubmitting) return;
    setProfileNotice(null);
    setIsPasswordSubmitting(true);
    try {
      const response = await fetch('/api/flymate/profile/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordForm),
      });
      const data = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(data.error || 'Unable to change password.');
      setPasswordForm({ currentPassword: '', newPassword: '' });
      setProfileNotice('Password updated.');
    } catch (profileError) {
      setProfileNotice(profileError instanceof Error ? profileError.message : 'Unable to change password.');
    } finally {
      setIsPasswordSubmitting(false);
    }
  }, [isPasswordSubmitting, passwordForm]);

  const startSocialLogin = React.useCallback(async (provider: SocialProvider) => {
    setAuthError(null);
    if (flyMateConfig?.socialAuth && flyMateConfig.socialAuth[provider] === false) {
      setAuthError('Social login is not enabled on this deployment yet. Use email registration or add the OAuth credentials in production.');
      return;
    }
    if (authMode === 'register' && (!leadForm.organization.trim() || !leadForm.jobTitle.trim() || !leadForm.combinedConsent)) {
      setAuthError('Enter your organization and role, then accept the terms before using social signup.');
      return;
    }
    try {
      const response = await fetch('/api/flymate/auth/social/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider,
          mode: authMode,
          organization: leadForm.organization.trim(),
          jobTitle: leadForm.jobTitle.trim(),
          combinedConsent: leadForm.combinedConsent,
          sessionId: sessionIdRef.current || undefined,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!response.ok || !data.url) throw new Error(data.error || 'Social login is unavailable.');
      window.location.href = data.url;
    } catch (socialError) {
      setAuthError(socialError instanceof Error ? socialError.message : 'Social login is unavailable.');
    }
  }, [authMode, flyMateConfig, leadForm]);

  const submitFeedback = React.useCallback(async (messageId: string, rating: 'up' | 'down') => {
    const targetMessage = messages.find((entry) => entry.id === messageId);
    if (!lead?.leadId || !targetMessage?.questionId) return;

    const draft = feedbackDrafts[messageId] || { why: '', expectedAnswer: '', expanded: rating === 'down' };

    if (rating === 'down' && !draft.why.trim() && !draft.expectedAnswer.trim()) {
      setFeedbackDrafts((current) => ({
        ...current,
        [messageId]: {
          ...draft,
          expanded: true,
        },
      }));
      return;
    }

    setFeedbackDrafts((current) => ({
      ...current,
      [messageId]: {
        ...draft,
        expanded: rating === 'down',
        saving: true,
      },
    }));

    try {
      const response = await fetch('/api/try-ai/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          leadId: lead.leadId,
          questionId: targetMessage.questionId,
          sessionId: sessionIdRef.current || undefined,
          rating,
          why: draft.why.trim(),
          expectedAnswer: draft.expectedAnswer.trim(),
        }),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.error || 'Failed to store FlyMate feedback.');
      }

      setMessages((current) =>
        current.map((entry) =>
          entry.id === messageId
            ? {
                ...entry,
                feedback: {
                  rating,
                  why: draft.why.trim(),
                  expectedAnswer: draft.expectedAnswer.trim(),
                  submittedAt: new Date().toISOString(),
                },
              }
            : entry
        )
      );
      setFeedbackDrafts((current) => ({
        ...current,
        [messageId]: {
          ...draft,
          expanded: false,
          saving: false,
        },
      }));
    } catch (feedbackError) {
      setError(feedbackError instanceof Error ? feedbackError.message : 'Failed to store FlyMate feedback.');
      setFeedbackDrafts((current) => ({
        ...current,
        [messageId]: {
          ...draft,
          expanded: true,
          saving: false,
        },
      }));
    }
  }, [feedbackDrafts, lead, messages]);

  const userDisplayName = currentUser ? `${currentUser.name} ${currentUser.surname}`.trim() || currentUser.email : formatLeadName(lead);
  const userInitials = (userDisplayName || 'FlyMate')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'FM';
  const accountPanel = (
    <button
      type="button"
      onClick={() => setIsProfileOpen(true)}
      className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3 text-left transition hover:bg-white/10"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">{userInitials}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-white">{userDisplayName || 'FlyMate account'}</span>
        <span className="block truncate text-xs text-slate-400">{currentUser ? currentUser.organization || currentUser.email : 'Register or login'}</span>
      </span>
      <span className="text-slate-500">›</span>
    </button>
  );

  return (
    <div className="h-dvh max-h-dvh w-full max-w-full overflow-hidden bg-[#212121] text-white">
      {isProfileOpen ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="FlyMate profile">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[28px] border border-white/10 bg-[#1f1f1f] p-6 shadow-2xl shadow-black/50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-base font-bold text-white">{userInitials}</span>
                <div>
                  <h2 className="text-xl font-semibold text-white">Profile</h2>
                  <p className="text-sm text-slate-400">{userDisplayName || 'FlyMate account'}</p>
                </div>
              </div>
              <button type="button" onClick={() => setIsProfileOpen(false)} className="rounded-full border border-white/10 px-3 py-2 text-sm text-slate-300 transition hover:bg-white/5 hover:text-white">Close</button>
            </div>

            {currentUser ? (
              <div className="mt-6 space-y-5">
                <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">User info</div>
                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <div><span className="text-slate-500">Email</span><div className="text-white">{currentUser.email}</div></div>
                    <div><span className="text-slate-500">Organization</span><div className="text-white">{currentUser.organization || 'Not set'}</div></div>
                    <div><span className="text-slate-500">Role</span><div className="text-white">{currentUser.jobTitle || 'Not set'}</div></div>
                  </div>
                </section>

                <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Subscription</div>
                  <div className="text-sm text-slate-300">Plan: <span className="font-semibold text-white">{currentUser.billing?.planId || 'free_beta'}</span></div>
                  <div className="mt-1 text-sm text-slate-300">Status: <span className="font-semibold text-white">{currentUser.billing?.status || 'active'}</span></div>
                  {!billingIsFree && flyMateConfig?.billing?.stripeEnabled ? (
                    <div className="mt-4 grid gap-2 sm:grid-cols-3">
                      {(flyMateConfig.billing.plans || []).map((plan) => (
                        <button key={plan.id} type="button" onClick={() => void choosePlan(plan.id)} className="rounded-xl border border-white/10 px-3 py-2 text-left text-xs text-slate-200 transition hover:bg-white/5">
                          {plan.name} {plan.monthlyTokenAllowance ? `, ${plan.monthlyTokenAllowance.toLocaleString()} tokens/month` : ''}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs leading-6 text-slate-500">Free access is enabled. Usage is still metered for future plans.</p>
                  )}
                </section>

                <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Token usage</div>
                  <div className="grid gap-3 text-sm sm:grid-cols-4">
                    <div><span className="text-slate-500">Requests</span><div className="text-white">{(usageSummary?.requests || 0).toLocaleString()}</div></div>
                    <div><span className="text-slate-500">Input</span><div className="text-white">{(usageSummary?.inputTokens || 0).toLocaleString()}</div></div>
                    <div><span className="text-slate-500">Output</span><div className="text-white">{(usageSummary?.outputTokens || 0).toLocaleString()}</div></div>
                    <div><span className="text-slate-500">Total</span><div className="text-white">{(usageSummary?.totalTokens || 0).toLocaleString()}</div></div>
                  </div>
                </section>

                <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Change password</div>
                  <form className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]" onSubmit={(event) => { event.preventDefault(); void changePassword(); }}>
                    <input type="password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))} placeholder="Current password" className="rounded-xl border border-white/10 bg-[#171717] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none" />
                    <input type="password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))} placeholder="New password" className="rounded-xl border border-white/10 bg-[#171717] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none" required />
                    <button type="submit" disabled={isPasswordSubmitting} className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#111111] transition hover:bg-slate-200 disabled:opacity-50">Update</button>
                  </form>
                  {profileNotice ? <p className="mt-3 text-xs text-amber-300">{profileNotice}</p> : null}
                </section>

                <button type="button" onClick={() => { setIsProfileOpen(false); void logout(); }} className="text-sm font-semibold text-slate-300 underline decoration-white/20 underline-offset-4 hover:text-white">Log out</button>
              </div>
            ) : (
              <div className="mt-6 space-y-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm leading-7 text-slate-300">
                <p>Log in or register to manage password, subscription, saved chats, and token usage.</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => openAccountAuth('login')}
                    className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#111111] transition hover:bg-slate-200"
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => openAccountAuth('register')}
                    className="inline-flex items-center justify-center rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/5"
                  >
                    Register
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <div className="flex h-full min-h-0 w-full max-w-full overflow-hidden">
        <aside className="hidden h-full min-h-0 w-[280px] shrink-0 overflow-hidden border-r border-white/10 bg-[#171717] lg:flex lg:flex-col">
          <div className="flex items-center justify-between px-4 py-4">
            <Link to="/" className="flex items-center gap-3 text-white transition hover:opacity-90">
              <img src={AVIAGOV_LOGO_URL} alt="AviaGov" className="h-8 w-auto" />
              <div className="text-sm font-semibold tracking-tight text-white">FlyMate</div>
            </Link>
            <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Guest
            </span>
          </div>

          <div className="px-3 pb-3">
            <button
              type="button"
              onClick={() => void startNewChatSession()}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/10"
            >
              <span className="text-base">＋</span>
              <span>New chat</span>
            </button>
          </div>

          <div className="px-3 pb-3">
            <Link
              to="/"
              className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
            >
              <span>↗</span>
              <span>Go to AviaGov</span>
            </Link>
          </div>

          <div className="px-3 pb-3">
            <Link
              to="/modules/ai-assistant"
              className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
            >
              <span>↖</span>
              <span>About FlyMate</span>
            </Link>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4">
            {currentUser && savedChats.length ? (
              <>
                <div className="mb-3 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Saved chats</div>
                <div className="mb-6 space-y-1.5">
                  {savedChats.map((chat) => (
                    <button
                      key={chat.sessionId}
                      type="button"
                      onClick={() => {
                        sessionIdRef.current = chat.sessionId;
                        if (typeof window !== 'undefined') window.localStorage.setItem(FLYMATE_CHAT_SESSION_ID_KEY, chat.sessionId);
                        void loadChatMessages(chat.sessionId);
                      }}
                      className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                    >
                      {summarizeChatLabel(chat.title)}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
            <div className="mb-3 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Recent prompts</div>
            <div className="space-y-1.5">
              {recentPrompts.length ? (
                recentPrompts.map((message) => (
                  <button
                    key={message.id}
                    type="button"
                    onClick={() => setInput(message.content)}
                    className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                    title={message.content}
                  >
                    {summarizeChatLabel(message.content)}
                  </button>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-white/10 px-3 py-3 text-sm text-slate-500">
                  Your prompts will appear here as this guest session grows.
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-white/10 px-4 py-4">
            {accountPanel}
          </div>
        </aside>

        {isMobileDrawerOpen ? (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="FlyMate menu">
            <button
              type="button"
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileDrawerOpen(false)}
              aria-label="Close FlyMate menu overlay"
            />
            <aside className="relative flex h-full w-[min(86vw,320px)] flex-col border-r border-white/10 bg-[#171717] shadow-2xl shadow-black/50">
              <div className="flex items-center justify-between px-4 py-4">
                <Link to="/" onClick={() => setIsMobileDrawerOpen(false)} className="flex items-center gap-3 text-white transition hover:opacity-90">
                  <img src={AVIAGOV_LOGO_URL} alt="AviaGov" className="h-8 w-auto" />
                  <div className="text-sm font-semibold tracking-tight text-white">FlyMate</div>
                </Link>
                <button
                  type="button"
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="rounded-full border border-white/10 px-3 py-1.5 text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                  aria-label="Close FlyMate menu"
                >
                  ×
                </button>
              </div>

              <div className="px-3 pb-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileDrawerOpen(false);
                    void startNewChatSession();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/10"
                >
                  <span className="text-base">＋</span>
                  <span>New chat</span>
                </button>
              </div>

              <div className="px-3 pb-3">
                <Link
                  to="/"
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                >
                  <span>↗</span>
                  <span>Go to AviaGov</span>
                </Link>
              </div>

              <div className="px-3 pb-3">
                <Link
                  to="/modules/ai-assistant"
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                >
                  <span>↖</span>
                  <span>About FlyMate</span>
                </Link>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4">
                {currentUser && savedChats.length ? (
                  <>
                    <div className="mb-3 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Saved chats</div>
                    <div className="mb-6 space-y-1.5">
                      {savedChats.map((chat) => (
                        <button
                          key={chat.sessionId}
                          type="button"
                          onClick={() => {
                            sessionIdRef.current = chat.sessionId;
                            if (typeof window !== 'undefined') window.localStorage.setItem(FLYMATE_CHAT_SESSION_ID_KEY, chat.sessionId);
                            void loadChatMessages(chat.sessionId);
                            setIsMobileDrawerOpen(false);
                          }}
                          className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                        >
                          {summarizeChatLabel(chat.title)}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
                <div className="mb-3 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Recent prompts</div>
                <div className="space-y-1.5">
                  {recentPrompts.length ? (
                    recentPrompts.map((message) => (
                      <button
                        key={message.id}
                        type="button"
                        onClick={() => {
                          setInput(message.content);
                          setIsMobileDrawerOpen(false);
                        }}
                        className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                        title={message.content}
                      >
                        {summarizeChatLabel(message.content)}
                      </button>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-white/10 px-3 py-3 text-sm text-slate-500">
                      Your prompts will appear here as this guest session grows.
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-white/10 px-4 py-4">
                {accountPanel}
              </div>
            </aside>
          </div>
        ) : null}

        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#212121]">
          <header className="shrink-0 flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 lg:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setIsMobileDrawerOpen(true)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-lg text-white transition hover:bg-white/5 lg:hidden"
                aria-label="Open FlyMate menu"
              >
                ☰
              </button>
              <Link to="/" className="shrink-0 transition hover:opacity-90 lg:hidden">
                <img src={AVIAGOV_LOGO_URL} alt="AviaGov" className="h-8 w-auto" />
              </Link>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">FlyMate</div>
                <div className="text-sm text-slate-300">Grounded aviation assistant</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="hidden rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/5 sm:inline-flex"
              >
                Go to AviaGov
              </Link>
              <span className="hidden rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-400 sm:inline-flex">
                No images, no model switching, grounded only
              </span>
              <button
                type="button"
                onClick={() => void startNewChatSession()}
                className="rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/5"
              >
                New chat
              </button>
            </div>
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 lg:px-8">
              <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
                {!lead && !isResolvingSession && (
                  <div className="mx-auto w-full max-w-2xl rounded-[24px] border border-white/10 bg-[#2a2a2a] p-4 shadow-2xl shadow-black/20 sm:p-5">
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">FlyMate account</div>
                    <h2 className="text-xl font-semibold text-white sm:text-2xl">{authMode === 'register' ? 'Register for FlyMate' : 'Login to FlyMate'}</h2>

                    <div className="mt-4 grid grid-cols-4 gap-2" aria-label={authMode === 'register' ? 'Social signup options' : 'Social login options'}>
                      <button type="button" onClick={() => void startSocialLogin('google')} className="inline-flex h-11 items-center justify-center rounded-2xl border border-white/10 text-slate-100 transition hover:bg-white/5" aria-label={`${authMode === 'register' ? 'Sign up' : 'Login'} with Google`}><GoogleIcon /></button>
                      <button type="button" onClick={() => void startSocialLogin('github')} className="inline-flex h-11 items-center justify-center rounded-2xl border border-white/10 text-slate-100 transition hover:bg-white/5" aria-label={`${authMode === 'register' ? 'Sign up' : 'Login'} with GitHub`}><GitHubIcon /></button>
                      <button type="button" onClick={() => void startSocialLogin('facebook')} className="inline-flex h-11 items-center justify-center rounded-2xl border border-white/10 text-slate-100 transition hover:bg-white/5" aria-label={`${authMode === 'register' ? 'Sign up' : 'Login'} with Facebook`}><FacebookIcon /></button>
                      <button type="button" onClick={() => void startSocialLogin('linkedin')} className="inline-flex h-11 items-center justify-center rounded-2xl border border-white/10 text-slate-100 transition hover:bg-white/5" aria-label={`${authMode === 'register' ? 'Sign up' : 'Login'} with LinkedIn`}><LinkedInIcon /></button>
                    </div>

                    <div className="mt-4 flex rounded-2xl border border-white/10 bg-[#1f1f1f] p-1 text-sm">
                      <button type="button" onClick={() => setAuthMode('register')} className={`flex-1 rounded-xl px-4 py-2 font-semibold ${authMode === 'register' ? 'bg-white text-[#111111]' : 'text-slate-400 hover:text-white'}`}>Register</button>
                      <button type="button" onClick={() => setAuthMode('login')} className={`flex-1 rounded-xl px-4 py-2 font-semibold ${authMode === 'login' ? 'bg-white text-[#111111]' : 'text-slate-400 hover:text-white'}`}>Login</button>
                    </div>

                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        void submitAuth();
                      }}
                      className="mt-4 space-y-3"
                    >
                      <div className={authMode === 'register' ? 'grid gap-3 sm:grid-cols-2' : 'grid gap-3'}>
                        <div className={authMode === 'register' ? '' : 'hidden'}>
                          <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Name</label>
                          <input
                            type="text"
                            value={leadForm.name}
                            onChange={(event) => setLeadForm((current) => ({ ...current, name: event.target.value }))}
                            className="w-full rounded-2xl border border-white/10 bg-[#1f1f1f] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-accent/40 focus:outline-none"
                            placeholder="Jane"
                            disabled={isAuthSubmitting}
                            required={authMode === 'register'}
                          />
                        </div>
                        <div className={authMode === 'register' ? '' : 'hidden'}>
                          <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Surname</label>
                          <input
                            type="text"
                            value={leadForm.surname}
                            onChange={(event) => setLeadForm((current) => ({ ...current, surname: event.target.value }))}
                            className="w-full rounded-2xl border border-white/10 bg-[#1f1f1f] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-accent/40 focus:outline-none"
                            placeholder="Doe"
                            disabled={isAuthSubmitting}
                            required={authMode === 'register'}
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Work email</label>
                          <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            spellCheck={false}
                            value={leadForm.email}
                            onChange={(event) => {
                              setLeadForm((current) => ({ ...current, email: event.target.value }));
                              if (verificationPending) {
                                setVerificationPending(false);
                                setVerificationCode('');
                              }
                            }}
                            className="w-full rounded-2xl border border-white/10 bg-[#1f1f1f] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-accent/40 focus:outline-none"
                            placeholder="jane@caa.gov.aero"
                            disabled={isAuthSubmitting}
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Password</label>
                          <input
                            type="password"
                            value={authForm.password}
                            onChange={(event) => setAuthForm((current) => ({ ...current, password: event.target.value }))}
                            className="w-full rounded-2xl border border-white/10 bg-[#1f1f1f] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-accent/40 focus:outline-none"
                            placeholder={authMode === 'register' ? 'Create password' : 'Password'}
                            disabled={isAuthSubmitting}
                            required
                          />
                        </div>
                        <div className={authMode === 'register' ? '' : 'hidden'}>
                          <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Organization</label>
                          <input
                            type="text"
                            value={leadForm.organization}
                            onChange={(event) => setLeadForm((current) => ({ ...current, organization: event.target.value }))}
                            className="w-full rounded-2xl border border-white/10 bg-[#1f1f1f] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-accent/40 focus:outline-none"
                            placeholder="Civil Aviation Authority"
                            disabled={isAuthSubmitting}
                            required={authMode === 'register'}
                          />
                        </div>
                        <div className={authMode === 'register' ? '' : 'hidden'}>
                          <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Role</label>
                          <input
                            type="text"
                            value={leadForm.jobTitle}
                            onChange={(event) => setLeadForm((current) => ({ ...current, jobTitle: event.target.value }))}
                            className="w-full rounded-2xl border border-white/10 bg-[#1f1f1f] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-accent/40 focus:outline-none"
                            placeholder="AIM Manager"
                            disabled={isAuthSubmitting}
                            required={authMode === 'register'}
                          />
                        </div>
                      </div>

                      <label className={authMode === 'register' ? 'flex items-start gap-3 rounded-2xl border border-white/10 bg-[#1f1f1f] p-4 text-sm text-slate-300' : 'hidden'}>
                        <input
                          type="checkbox"
                          checked={leadForm.combinedConsent}
                          onChange={(event) => setLeadForm((current) => ({ ...current, combinedConsent: event.target.checked }))}
                          className="mt-1 h-4 w-4 rounded border-white/20 bg-[#1f1f1f] text-accent focus:ring-accent"
                          disabled={isAuthSubmitting}
                          required={authMode === 'register'}
                        />
                        <span>
                          I accept the <Link to="/terms" className="text-accent hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>, and I agree to receive AviaGov email updates and product communications.
                        </span>
                      </label>

                      {authError ? <p className="text-sm text-amber-300">{authError}</p> : null}

                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs leading-6 text-slate-500">
                          {authMode === 'register' ? 'Your organization and role are linked to your FlyMate account.' : 'Your saved chats and n8n memory key are loaded after login.'}
                        </p>
                        <button
                          type="submit"
                          disabled={isAuthSubmitting}
                          className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#111111] transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isAuthSubmitting ? 'Please wait...' : authMode === 'register' ? 'Register and start' : 'Login'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {lead && messages.length <= 1 ? (
                  <div className="mx-auto flex min-h-[40vh] w-full max-w-3xl flex-col items-center justify-center text-center">
                    <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">What do you want to ask today?</h1>
                    <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
                      Ask grounded ICAO, AIM, eAIP, PANS-OPS, compliance, weather-message, or aviation systems questions. FlyMate should answer only when the evidence supports it.
                    </p>
                  </div>
                ) : null}

                <div className="flex flex-col gap-5">
                  {messages.map((message) => (
                    <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`w-full ${message.role === 'user' ? 'max-w-2xl' : 'max-w-3xl'}`}>
                        <div className={`rounded-[24px] px-5 py-4 ${message.role === 'user' ? 'bg-[#303030] text-white' : 'bg-transparent text-slate-100'}`}>
                          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
                            {flyMateMessageLabel(message)}
                          </div>
                          <p className="whitespace-pre-wrap text-sm leading-7 sm:text-[15px]">{message.content}</p>

                          {message.role === 'user' && Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {message.attachments.map((attachment, index) => (
                                <span
                                  key={`${message.id}-attachment-${index}`}
                                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-200"
                                >
                                  <span aria-hidden="true">📎</span>
                                  <span>{attachment.name || 'Uploaded file'}</span>
                                </span>
                              ))}
                            </div>
                          ) : null}

                          {message.role === 'assistant' && Array.isArray(message.sources) && message.sources.length > 0 && (
                            <div className="mt-5">
                              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Sources</div>
                              <div className="flex flex-wrap gap-2.5">
                                {message.sources.map((source, index) => (
                                  <span
                                    key={`${message.id}-source-${index}`}
                                    title={source.citation || source.documentName || 'ICAO reference'}
                                    className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white"
                                  >
                                    {source.documentName || 'ICAO reference'}
                                    {typeof source.page === 'number' ? `, p. ${source.page}` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {message.role === 'assistant' && message.artifact?.requested && message.artifact.contentMarkdown ? (
                            <div className="mt-5 rounded-2xl border border-cyan-300/20 bg-cyan-300/10 px-4 py-3">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-200">
                                    Prepared document
                                  </div>
                                  <div className="mt-1 text-sm font-semibold text-white">
                                    {message.artifact.title || 'FlyMate document'}
                                  </div>
                                  <div className="mt-1 text-xs leading-5 text-slate-400">
                                    {canUploadFiles
                                      ? 'Download as Markdown, DOCX, PDF, XLSX, PPTX, text, HTML, CSV, JSON, or XML.'
                                      : 'File downloads are available on paid FlyMate plans.'}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 sm:justify-end">
                                  {ARTIFACT_DOWNLOAD_FORMATS.map((item) => (
                                    <button
                                      key={item.format}
                                      type="button"
                                      onClick={() => void downloadArtifact(message.artifact as ChatArtifact, item.format)}
                                      disabled={!canUploadFiles}
                                      className="rounded-full bg-white px-3 py-2 text-xs font-semibold text-[#111111] transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      {item.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : null}

                          {message.role === 'assistant' && message.questionId ? (
                            <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                  Was this answer helpful?
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => void submitFeedback(message.id, 'up')}
                                    disabled={feedbackDrafts[message.id]?.saving || Boolean(message.feedback?.rating)}
                                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition ${message.feedback?.rating === 'up' ? 'border-emerald-400 bg-emerald-500/15 text-emerald-200' : 'border-white/10 bg-transparent text-slate-300 hover:bg-white/5'} disabled:opacity-60`}
                                  >
                                    👍
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setFeedbackDrafts((current) => ({
                                      ...current,
                                      [message.id]: {
                                        why: current[message.id]?.why || '',
                                        expectedAnswer: current[message.id]?.expectedAnswer || '',
                                        expanded: true,
                                        saving: false,
                                      },
                                    }))}
                                    disabled={feedbackDrafts[message.id]?.saving || Boolean(message.feedback?.rating)}
                                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition ${message.feedback?.rating === 'down' ? 'border-amber-400 bg-amber-500/15 text-amber-200' : 'border-white/10 bg-transparent text-slate-300 hover:bg-white/5'} disabled:opacity-60`}
                                  >
                                    👎
                                  </button>
                                </div>
                              </div>

                              {message.feedback?.rating ? (
                                <div className="mt-3 text-sm text-slate-400">
                                  Thanks, your feedback was recorded.
                                </div>
                              ) : feedbackDrafts[message.id]?.expanded ? (
                                <div className="mt-4 space-y-3">
                                  <textarea
                                    value={feedbackDrafts[message.id]?.why || ''}
                                    onChange={(event) => setFeedbackDrafts((current) => ({
                                      ...current,
                                      [message.id]: {
                                        why: event.target.value,
                                        expectedAnswer: current[message.id]?.expectedAnswer || '',
                                        expanded: true,
                                        saving: current[message.id]?.saving,
                                      },
                                    }))}
                                    placeholder="Why was this answer not good enough?"
                                    className="min-h-[90px] w-full rounded-2xl border border-white/10 bg-[#262626] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none"
                                  />
                                  <textarea
                                    value={feedbackDrafts[message.id]?.expectedAnswer || ''}
                                    onChange={(event) => setFeedbackDrafts((current) => ({
                                      ...current,
                                      [message.id]: {
                                        why: current[message.id]?.why || '',
                                        expectedAnswer: event.target.value,
                                        expanded: true,
                                        saving: current[message.id]?.saving,
                                      },
                                    }))}
                                    placeholder="What answer were you expecting?"
                                    className="min-h-[110px] w-full rounded-2xl border border-white/10 bg-[#262626] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none"
                                  />
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => void submitFeedback(message.id, 'down')}
                                      disabled={feedbackDrafts[message.id]?.saving}
                                      className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#111111] transition hover:bg-slate-200 disabled:opacity-60"
                                    >
                                      {feedbackDrafts[message.id]?.saving ? 'Saving...' : 'Send feedback'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setFeedbackDrafts((current) => ({
                                        ...current,
                                        [message.id]: {
                                          why: current[message.id]?.why || '',
                                          expectedAnswer: current[message.id]?.expectedAnswer || '',
                                          expanded: false,
                                          saving: false,
                                        },
                                      }))}
                                      className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:bg-white/5"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}

                  {isSubmitting && (
                    <div className="flex justify-start">
                      <div className="w-full max-w-3xl rounded-[24px] px-5 py-4 text-slate-100">
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
                          {isRecoveringAnswer ? 'FlyMate is checking for your answer' : 'FlyMate is grounding the answer'}
                        </div>
                        <div className="flex items-center gap-2 text-slate-400">
                          <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                          <span className="h-2 w-2 animate-pulse rounded-full bg-white [animation-delay:150ms]" />
                          <span className="h-2 w-2 animate-pulse rounded-full bg-white [animation-delay:300ms]" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>
            </div>

            <div className="shrink-0 border-t border-white/10 bg-[#212121] px-4 py-4 lg:px-8">
              <div className="mx-auto w-full max-w-4xl">
                {lead ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      void sendMessage();
                    }}
                    className="space-y-3"
                  >
                    <div className="rounded-[28px] border border-white/10 bg-[#2a2a2a] transition-colors focus-within:border-white/20">
                      <textarea
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            void sendMessage();
                          }
                        }}
                        placeholder="Ask FlyMate about ICAO, AIM, eAIP, PANS-OPS, aviation messages, aeronautical systems, or aviation compliance topics..."
                        className="max-h-[28vh] min-h-[72px] w-full resize-none bg-transparent px-5 py-4 text-sm text-white placeholder:text-slate-500 focus:outline-none sm:text-[15px]"
                        disabled={isSubmitting}
                      />

                      <div className="flex flex-col gap-3 border-t border-white/10 px-4 py-3">
                        {selectedFiles.length ? (
                          <div className="flex flex-wrap gap-2 text-xs text-slate-300">
                            {selectedFiles.map((file) => (
                              <span key={`${file.name}-${file.size}`} className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                                {file.name}
                              </span>
                            ))}
                            <button type="button" onClick={() => setSelectedFiles([])} className="text-slate-400 underline decoration-white/20 underline-offset-4 hover:text-white">Clear files</button>
                          </div>
                        ) : null}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span>Enter to send</span>
                            <span>•</span>
                            <span>Shift+Enter for a new line</span>
                            <span>•</span>
                            <span>{canUploadFiles ? `Files enabled: up to ${maxUploadFiles} files, ${maxUploadFileSizeMb} MB each` : 'Files available on paid plans'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className={`inline-flex cursor-pointer items-center justify-center rounded-full border border-white/10 px-4 py-2.5 text-sm font-semibold transition ${canUploadFiles && !isSubmitting ? 'text-white hover:bg-white/5' : 'cursor-not-allowed text-slate-600 opacity-60'}`}>
                              Attach
                              <input
                                type="file"
                                multiple
                                accept=".pdf,.docx,.txt,.md,.csv,.json,.xml,.aixm,.iwxxm,.fixm,text/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/json,application/xml"
                                className="hidden"
                                disabled={!canUploadFiles || isSubmitting}
                                onChange={(event) => {
                                  const files = Array.from(event.target.files || []).slice(0, maxUploadFiles);
                                  const oversizedFile = files.find((file) => file.size > maxUploadFileSizeBytes);
                                  if (oversizedFile) {
                                    setError(`${oversizedFile.name} is too large. FlyMate accepts files up to ${maxUploadFileSizeMb} MB.`);
                                    setSelectedFiles([]);
                                    event.target.value = '';
                                    return;
                                  }
                                  setError(null);
                                  setSelectedFiles(files);
                                  event.target.value = '';
                                }}
                              />
                            </label>
                            <button
                              type="submit"
                              disabled={isSubmitting || !input.trim()}
                              className="inline-flex items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#111111] transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isSubmitting ? 'Grounding...' : 'Send'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {error ? <p className="text-sm text-amber-300">{error}</p> : null}
                  </form>
                ) : isResolvingSession ? (
                  <div className="rounded-[28px] border border-white/10 bg-[#2a2a2a] px-5 py-4 text-sm text-slate-400">
                    Restoring your FlyMate session...
                  </div>
                ) : (
                  <div className="rounded-[28px] border border-dashed border-white/10 bg-[#2a2a2a] px-5 py-4 text-sm text-slate-500">
                    Complete the FlyMate introduction form to unlock the chat.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlyMateShell;
